/** @jsxImportSource @emotion/react */
import {
  ClockCircleOutlined,
  DownOutlined,
  ExportOutlined,
  RightOutlined,
  UserOutlined,
  PlusCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { formatCurrency, formatDate } from '@sportninja/common/utils/utils'
import { Alert, App, Image, Input, QRCode, Steps } from 'antd'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import { RegistrationContext } from '../context/RegistrationContext'
import {
  PageTitle,
  buttonStyle,
  headingSixStyle,
  headingStyle,
  paragraphMediumStyle,
  paragraphSmallBoldStyle,
  paragraphSmallStyle,
  paragraphXSmallStyle,
} from './Main'
import { ROUTES } from '@sportninja/common/constants/app'
import { calculateDiscountAndFormatCurrency } from './New_TeamRegister'
import { weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { useBlockNavigation } from '../hooks/useBlockNavigation'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import WarningModal from 'src/components/WarningModal'
import ShareIcon from 'src/components/icons/ShareIcon'
import ShareModal from 'src/components/ShareModal'
import appOrigin from '@sportninja/common/constants/appOrigin'
import { buildRegistrationUrl } from '@sportninja/common/features/registration/registrationUrlParams'
import { isRegistrationTeamPrivate } from '@sportninja/common/features/registration/startStep/privateTeams'
import { TEAM_BUDGET_PAYMENT_COPY } from '@sportninja/common/features/registration/teamBudgetPayment/constants/copy'
import {
  payPathWithReturn,
  teamBudgetReturnPath,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/payPath'
import { opensPayPage } from '@sportninja/common/features/registration/teamBudgetPayment/utils/repPaymentPresentation'
import Button from 'src/components/ts-components/Button'
import { resolveCardDeposit } from 'src/pages/RegistrationPreview/components/SelectedDivisionCard/utils'
import PrivateTeamBadge from '../components/PrivateTeamBadge'
import RepPaymentStatusSkeleton from '../components/RepPaymentStatusSkeleton'
import ShareLinkField from '../components/ShareLinkField'
import { useRepPaymentStatus } from '../hooks/useRepPaymentStatus'
import { REGISTRATION_STEPS, registrationStepPath } from '../registrationSteps'
import { withFreshSession } from '../utils/freshRegistration'
import {
  resolveAutoApproveForType,
  resolveIsApproved,
  resolveMyTeamRoute,
  resolveShareLink,
  resolvePaymentStepPresentation,
} from '../utils/reviewSummary'
import {
  resolveReviewRepPaymentView,
  resolveTeamBudgetReview,
} from '../utils/reviewRepPayment'

async function updateTeamLogo(teamId, base64) {
  const res = await req(`/teams/${teamId}/image`, {
    method: 'POST',
    body: base64,
  })

  return res
}

/*
 * SN-4709 (Jeff, Sep 1) — "What's Next" REDESIGN.
 *
 * "This looks so trash… very unreadable… taking another stab at the screen would be
 * really beneficial."
 *
 * What was actually wrong, and what each fix answers:
 *
 *  1. EVERY ROW LOOKED IDENTICAL. A pure navigation link ("Go To My Competition") was
 *     rendered exactly like an expandable panel containing a live file upload — same
 *     background, same PRIMARY text, same chevron. There are two different KINDS of thing
 *     here, so there are now two variants: an ACTION (expands, does something in place)
 *     and a LINK (leaves for somewhere else).
 *
 *  2. THE CHEVRON LIED. A `route` row with no content still rendered a right-chevron, the
 *     universal "there is more inside" affordance, when there was nothing inside. Links
 *     now carry an external-link icon, which is what they actually do.
 *
 *  3. NESTED INTERACTIVE ELEMENTS. The expander lived INSIDE the anchor, so on a row with
 *     both a route and content, expanding it also navigated away in a new tab. A row is
 *     now one thing or the other, and that is structural rather than a matter of care.
 *
 *  4. NO HIERARCHY AND NO WORDS. Every row was a bare blue phrase. Each now carries a
 *     one-line subtitle, so the panel reads as guidance instead of a list of links.
 *
 * House rules: ts-components `Text` rather than raw <p> + style constants, Title Case
 * headings, no hand-rolled buttons.
 */
const nextRowStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  /*
   * Part of the min-width chain described on the What's Next column below. A row
   * whose expanded content refuses to wrap (the share link) would otherwise set
   * this card's min-content width to that content's full width, and the card
   * hands that straight up to the column.
   */
  min-width: 0;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${colors.NEUTRAL_600};
  background: ${colors.NEUTRAL_700};
  @media (max-width: 768px) {
    width: 100%;
  }
`

const nextRowHeaderStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`

const nextRowTextStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
  min-width: 0;
  text-align: left;
`

/** Title and its optional state badge, on one baseline. */
const nextRowTitleStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

/**
 * An inline route inside body copy. It takes the tenant flair rather than the
 * body colour, because the surrounding sentence is the only thing telling you it
 * is clickable at all.
 */
const inlineLinkStyle = css`
  color: ${colors.PRIMARY};
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.85;
  }
`

/** Sep 24 — the payment status's description, then its PAY NOW / CONTINUE PAYMENT. */
const repPaymentActionStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

/**
 * A row that DOES something here: expands to reveal its own controls.
 *
 * The chevron is honest — it only appears because there really is content behind it —
 * and the whole header is the toggle, so the hit target matches what it looks like.
 */
const NextAction = ({
  title,
  subtitle,
  content,
  defaultOpen = false,
  titleAccessory = null,
}) => {
  const [isOpened, setIsOpened] = useState(defaultOpen)

  return (
    <div css={nextRowStyle}>
      <div
        role='button'
        tabIndex={0}
        onClick={() => setIsOpened(!isOpened)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setIsOpened(!isOpened)
          }
        }}
        css={[
          nextRowHeaderStyle,
          css`
            cursor: pointer;
          `,
        ]}
      >
        <div css={nextRowTextStyle}>
          {/*
            A row's STATE sits beside its title, not inside it: "Invite Players —
            Private Team" made the title carry a badge's job, and grew the one
            string on the card that has to stay scannable.
          */}
          <div css={nextRowTitleStyle}>
            <Text
              variant='paragraphSmall'
              weight='semiBold'
              color={colors.WHITE}
            >
              {capitalizeWords(title)}
            </Text>
            {titleAccessory}
          </div>
          {subtitle ? (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {subtitle}
            </Text>
          ) : null}
        </div>
        {/*
          TRAILING, like NextLink's ExportOutlined. A leading chevron reserved
          left-gutter space that the link rows had no equivalent of, so the two
          kinds of card started their titles at different x and the stack of
          them read as ragged. Trailing is also the conventional place for a
          disclosure affordance. The whole header stays the toggle target.
        */}
        {isOpened ? (
          <DownOutlined
            css={css`
              color: ${colors.PRIMARY};
            `}
          />
        ) : (
          <RightOutlined
            css={css`
              color: ${colors.PRIMARY};
            `}
          />
        )}
      </div>
      {isOpened && content ? <>{content}</> : null}
    </div>
  )
}

/**
 * A row that takes you SOMEWHERE ELSE. No chevron, no toggle state, nothing nested
 * inside the anchor that could fight it for the click.
 */
const NextLink = ({ title, subtitle, route }) => (
  <a href={route} target='_blank' rel='noreferrer' css={nextRowStyle}>
    <div css={nextRowHeaderStyle}>
      <div css={nextRowTextStyle}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.PRIMARY}>
          {capitalizeWords(title)}
        </Text>
        {subtitle ? (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
      <ExportOutlined
        css={css`
          color: ${colors.PRIMARY};
        `}
      />
    </div>
  </a>
)

/**
 * A row that opens something HERE — a modal — rather than expanding in place or
 * leaving the page.
 *
 * It is a real <button>: the row does something on click, so it must be one.
 * There is nothing behind it to disclose, so it takes NextLink's trailing icon
 * treatment rather than NextAction's chevron, which would promise content that
 * does not exist (see point 2 of the redesign note above).
 */
const NextButton = ({ title, subtitle, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    css={[
      nextRowStyle,
      css`
        /*
         * nextRowStyle was written for a div/anchor, so the UA button styles it
         * does not overwrite have to go: a button brings its own centred text
         * and its own font, neither of which matches the rows beside it.
         */
        width: 100%;
        cursor: pointer;
        text-align: left;
        font: inherit;
      `,
    ]}
  >
    <div css={nextRowHeaderStyle}>
      <div css={nextRowTextStyle}>
        <Text variant='paragraphSmall' weight='semiBold' color={colors.PRIMARY}>
          {capitalizeWords(title)}
        </Text>
        {subtitle ? (
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
      {/* The shared SVG every share trigger uses, NOT antd's ShareAltOutlined —
          the two draw different glyphs and the arrow is the established mark.
          Colour is passed explicitly: this SVG takes a `color` prop and
          defaults to SOLITUDE, so it does not inherit from the row. */}
      <ShareIcon size={16} color={colors.PRIMARY} />
    </div>
  </button>
)

/**
 * Kept as the single entry point the panel below calls, so the four existing rows did not
 * all have to be rewritten at their call sites: a `route` makes it a link, an `onClick`
 * makes it a button, anything else makes it an action.
 */
const NextContent = ({
  title,
  subtitle,
  content,
  route,
  onClick,
  defaultOpen,
  titleAccessory,
}) =>
  route ? (
    <NextLink title={title} subtitle={subtitle} route={route} />
  ) : onClick ? (
    <NextButton title={title} subtitle={subtitle} onClick={onClick} />
  ) : (
    <NextAction
      title={title}
      subtitle={subtitle}
      content={content}
      defaultOpen={defaultOpen}
      titleAccessory={titleAccessory}
    />
  )

const accept = 'image/*'

const RegisterReview = () => {
  const {
    division,
    selectedTeam,
    registration,
    session,
    email,
    currencyCode,
    couponInfo,
    role,
    firstName,
    lastName,
    selectedPlayer,
    isTeamRep,
    userInfo,
    setSession,
  } = useContext(RegistrationContext)

  // The registration id comes from the route — the context's `id` is never
  // populated (nothing calls its setter), so it would build a broken URL.
  const { id } = useParams()

  const hasCouponAppliedToDeposit = useMemo(() => {
    return (
      couponInfo &&
      couponInfo?.is_deposit &&
      couponInfo.deposit_discount_amount > 0
    )
  }, [couponInfo])

  const hasCouponAppliedToCost = useMemo(() => {
    return couponInfo && couponInfo?.discount > 0 && couponInfo?.is_installment
  }, [couponInfo])

  const { message } = App.useApp()

  // This screen is terminal — the registration is already submitted, so going
  // back can't change it and would only strand the user on a stale step. Block
  // every route exit (in-app and the browser Back button) and offer the only
  // meaningful action: start a new registration. Applies whenever this screen
  // is rendered, including on a pasted /review URL. A boolean is idempotent, so
  // rapid repeated Back presses can't stack modals.
  const [isLeaveWarningOpen, setIsLeaveWarningOpen] = useState(false)

  useBlockNavigation({
    when: true,
    onAttempt: () => setIsLeaveWarningOpen(true),
  })

  // Start over: open a fresh registration in a NEW TAB, so the new session gets a
  // clean history stack with no back-entries into this finished registration.
  // Shared by the "New Registration" link and the back-navigation modal.
  //
  // `window.open` must run SYNCHRONOUSLY, as the first statement — any await
  // before it consumes the click's user-activation and the browser blocks the
  // open as a popup. Keep it first even if async work is added later.
  const startNewRegistration = () => {
    // No `noopener` here: with it, some browsers return null even on a SUCCESSFUL
    // open (the handle is severed by design), which is indistinguishable from a
    // blocked popup. We need the handle to tell those two apart. `_blank` alone
    // already gives the new tab its own history stack, which is the point.
    // `?nreg=1` tells the new tab to ignore the stored session and create its
    // own. Without it the tab rehydrates `registration_session_<id>` from
    // localStorage (shared across tabs) and resumes THIS finished session.
    const freshRegistration = `${registrationStepPath(
      id,
      REGISTRATION_STEPS.SELECT_YOUR_DIVISION
    )}${withFreshSession('')}`
    const freshTab = window.open(freshRegistration, '_blank')

    // Popup blocked (or invoked without a real click). Fall back to starting the
    // new registration in this tab rather than leaving the user with no way to
    // start over at all. The finished session is kept here too — never deleted:
    // the server refuses to delete one a Team Budget payment was taken for
    // (SN-8441), and nothing needs it gone.
    if (!freshTab) {
      window.location.assign(freshRegistration)
      return
    }

    // Sever the back-reference now that we've confirmed the open succeeded. The
    // target is same-origin so this isn't a security fix, just hygiene — nothing
    // should be reaching back into this tab.
    freshTab.opener = null

    // The current session is deliberately left intact: this tab stays on /review,
    // so the user keeps their confirmation screen, and the new tab starts its own
    // session independently.

    // Close the modal — otherwise it lingers over the confirmation screen.
    setIsLeaveWarningOpen(false)
  }

  const isPlayerRegistration = useMemo(
    () => role?.name === 'Player',
    [role?.name]
  )

  const [isShareOpen, setIsShareOpen] = useState(false)
  const waiverRequired = registration?.registration?.show_waiver
  /*
   * SN-4709 (Jeff, Sep 1) — the team the confirm JUST CREATED, and its share link.
   *
   * confirmTeamRegistration persists the new TeamRegistration onto the session, so the
   * server can hand back a REAL status and the one canonical share link. Everything below
   * reads from that rather than re-deriving anything client-side.
   */
  const confirmedTeam = session?.linked_team_registration ?? null
  const isTeamRegistration = !isPlayerRegistration
  const shareLink = resolveShareLink(confirmedTeam, isPlayerRegistration)

  /*
   * SN-4709 (Jeff, Sep 1) — both answers come from `utils/reviewSummary`, which carries
   * the reasoning and the tests. They were inline expressions on a component with a
   * ~30-field context, which is why neither had a test.
   */
  const autoApprove = useMemo(
    () =>
      resolveAutoApproveForType(
        registration?.registration,
        isPlayerRegistration
      ),
    [registration?.registration, isPlayerRegistration]
  )

  const teamCost = useMemo(() => {
    return calculateDiscountAndFormatCurrency(
      couponInfo?.discount,
      couponInfo?.type || couponInfo?.discount_type,
      isPlayerRegistration ? division?.player_cost : division?.team_cost,
      currencyCode
    )
  }, [
    couponInfo,
    isPlayerRegistration,
    division?.player_cost,
    division?.team_cost,
    currencyCode,
  ])

  const isZero = useMemo(() => {
    const baseCost =
      Number(
        isPlayerRegistration ? division?.player_cost : division?.team_cost
      ) || 0
    return baseCost === 0
  }, [isPlayerRegistration, division?.player_cost, division?.team_cost])

  const {
    team_name,
    division_name,
    paid,
    team_id,
    billing_schedule,
    root_schedule_id,
  } = session

  /**
   * The registration link that brings someone else onto this same team.
   *
   * Built from the shared builder so it stays the shape the wizard reads back
   * (`/registration/:id?team_id=`), and from `appOrigin` so a link copied on
   * localhost still points at the tenant's real app.
   *
   * Offered for a PLAYER registration only: a `team_id` link registers a
   * player onto an existing roster, which is not what a team rep's
   * confirmation is about. Drafts are included — the start step lets a player
   * register into one, so the link works there the same way.
   *
   * SN-4709 Team Privacy — and never for a PRIVATE team. Who may join one is
   * the rep's decision: they hold the share link and hand it out themselves.
   * A player passing this link on would be re-sharing an invitation that was
   * given to them alone.
   */
  const isPrivateTeam = isRegistrationTeamPrivate(registration?.teams, team_id)
  const canShareRegistration = Boolean(
    isPlayerRegistration && id && team_id && !isPrivateTeam
  )
  const shareUrl = useMemo(
    () =>
      canShareRegistration
        ? `${appOrigin}${buildRegistrationUrl(id, { teamId: team_id })}`
        : '',
    [canShareRegistration, id, team_id]
  )
  const shareTeamName = team_name || selectedTeam?.name_full || 'My Team'

  const defferPayment = session?.defer_payment

  /*
   * Sep 24 (B-7) — a Team Budget rep may have paid toward the team fee at
   * registration (or on the pay page, for an invoice from before SN-8441). What they
   * paid is read from the session as the server holds it NOW: the wizard's own copy
   * can predate it. F1 — also when the division is no longer Team Budget but the
   * session proves the payment; its money shows in the currency it was taken in.
   */
  const { hasTeamBudgetHistory, isTeamBudgetTeam } = resolveTeamBudgetReview({
    isTeamRegistration,
    division,
    session,
  })
  const repPaymentStatus = useRepPaymentStatus({
    sessionId: session?.id,
    enabled: isTeamBudgetTeam,
  })
  // The registration's REAL status — the fresh one once this entry's refetch answers
  // (the wizard's copy predates a payment made on the pay page).
  const isApproved = resolveIsApproved(
    repPaymentStatus.linkedTeamRegistration ?? confirmedTeam,
    autoApprove
  )
  const repPaymentView = resolveReviewRepPaymentView({
    isTeamBudgetTeam,
    division,
    status: repPaymentStatus,
    currencyCode,
    formatCurrency,
  })

  /**
   * PAY NOW / CONTINUE PAYMENT: the pay page for the rep's invoice, which brings
   * them back to this step of this session. A full navigation — this step blocks
   * in-app navigation (useBlockNavigation), and a cold return reloads the session
   * from the URL.
   */
  const openRepPayment = async () => {
    // Ask again first: a payment that settled — or started where the pay page
    // cannot resume it — since the row was drawn shows here as it now is, rather
    // than sending the rep to a bill the pay page would refuse.
    const fresh = await repPaymentStatus.refresh()
    if (!opensPayPage(fresh) || !session?.id) {
      return
    }
    window.location.assign(
      payPathWithReturn(
        `${ROUTES.REGISTRATION_PAYMENT}?billingId=${encodeURIComponent(
          fresh.billing_id
        )}`,
        teamBudgetReturnPath(
          registrationStepPath(id, REGISTRATION_STEPS.REGISTER_REVIEW),
          session.id
        )
      )
    )
  }

  /*
   * SN-8326 / SN-8344 — title, status, icon and description as ONE answer.
   *
   * They used to be four independent ternaries on `paid`, and `paid` is true for a
   * Team-Managed Budget registration that has paid nothing (the server completes a
   * zero-total session on purpose), so the row read "Payment Approved" with a green
   * check. Changing only the label would have left the check and the finished state
   * contradicting it, so all four now come from one tested predicate.
   */
  const paymentStep = useMemo(
    () =>
      resolvePaymentStepPresentation({
        costDistributionEnabled: division?.cost_distribution_enabled,
        paid,
        deferPayment: defferPayment,
        isPlayerRegistration,
        repPayment: repPaymentView,
        hasTeamBudgetHistory,
      }),
    [
      division?.cost_distribution_enabled,
      paid,
      defferPayment,
      isPlayerRegistration,
      repPaymentView,
      hasTeamBudgetHistory,
    ]
  )

  console.log('defferPayment', defferPayment)

  /*
   * Sep 24 (B-2, review F4) — the card states a Team Budget rep's deposit as the
   * division's Minimum To Qualify and applies no coupon (the fee is split among the
   * players), exactly as step 3 does; every other registration reads as before.
   */
  const cardDeposit = resolveCardDeposit({
    division: division ?? {},
    isTeamRep: !isPlayerRegistration,
    hasCoupon: true,
  })
  const depositValue = cardDeposit.deposit
  const showsCoupons = cardDeposit.couponApplies

  const [installmentScheduleOpen, setInstallmentScheduleOpen] = useState(false)
  const iosAppLink = isCanlan
    ? 'https://apps.apple.com/ca/app/canlan-sports-stats/id1606251092'
    : 'https://apps.apple.com/br/app/sportninja/id1436064312'
  const androidAppLink = isCanlan
    ? 'https://play.google.com/store/apps/details?id=com.sportninjainc.canlansports'
    : 'https://play.google.com/store/apps/details?id=com.sportninjainc.sportninja'
  const [newTeamImage, setNewTeamImage] = useState(null)
  const [newTeamLoading, setNewTeamLoading] = useState(false)
  const [newTeamError, setNewTeamError] = useState(null)
  const [newTeamSuccess, setNewTeamSuccess] = useState(null)
  const [newPlayerImage, setNewPlayerImage] = useState(null)
  const [newPlayerLoading, setNewPlayerLoading] = useState(false)
  const [newPlayerError, setNewPlayerError] = useState(null)
  const [newPlayerSuccess, setNewPlayerSuccess] = useState(null)

  const playerDisplayName = useMemo(() => {
    // If not a player registration, return empty string
    if (!isPlayerRegistration) {
      return ''
    }

    // If there's a player name in the session, use it
    if (session?.player?.name_first && session?.player?.name_last) {
      return `${session?.player?.name_first} ${session?.player?.name_last}`
    }

    // If there's a selected existing player, use their first and last name
    if (selectedPlayer && selectedPlayer?.id !== 'new') {
      return `${selectedPlayer?.name_first || ''} ${
        selectedPlayer?.name_last || ''
      }`
    }

    // For new players, use firstName/lastName if provided
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }

    // Fall back to userInfo first name or empty string
    return `${userInfo?.name_first || ''} ${userInfo?.name_last || ''}`
  }, [
    isPlayerRegistration,
    session?.player?.name_first,
    session?.player?.name_last,
    selectedPlayer,
    firstName,
    lastName,
    userInfo?.name_first,
    userInfo?.name_last,
  ])

  useEffect(() => {
    message.open({
      type: 'success',
      content: `${
        isPlayerRegistration ? 'Player Registration' : 'Team Registration'
      } submitted successfully`,
      duration: 4,
    })
  }, [isPlayerRegistration, message])

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: center;
          background-color: ${colors.NEUTRAL_800};
          width: 1080px;
          padding: 0 35px 35px 35px;
          border-radius: 16px 16px 0px 0px;
          @media (max-width: 768px) {
            min-width: 0;
            width: 100%;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            align-self: stretch;
            margin-top: 16px;
            margin-bottom: -8px;
            @media (max-width: 768px) {
              flex-direction: column;
              width: 100%;
              gap: 8px;
            }
          `}
        >
          <a
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: pointer;
            `}
            href={`${ROUTES.PROFILE.ROOT}`}
            target='_blank'
            rel='noreferrer'
          >
            <UserOutlined
              css={css`
                color: ${colors.PRIMARY};
              `}
            />
            <p
              css={css`
                ${paragraphSmallStyle}
                color: ${colors.PRIMARY};
              `}
            >
              {email}{' '}
            </p>
          </a>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: pointer;
            `}
            onClick={startNewRegistration}
          >
            <PlusCircleOutlined
              css={css`
                color: ${colors.PRIMARY};
              `}
            />
            <p
              css={css`
                ${paragraphSmallStyle}
                color: ${colors.PRIMARY};
              `}
            >
              {'New Registration'}
            </p>
          </div>
        </div>
        <EventCard eventInfo={registration} />
        <EventProgress
          eventProgress={6}
          isWaiverRequired={waiverRequired}
          isTeamRep={isTeamRep}
        />
        <PageTitle
          title={`${
            isPlayerRegistration ? 'Player Registration' : 'Team Registration'
          } Submitted`}
          /*
           * SN-8326 — the legacy subtitle promised "the receipt for your payment" on
           * every registration, including a Team-Managed Budget one where nothing was
           * charged and no receipt exists. Same predicate as the status row, so the two
           * statements on this screen cannot contradict each other.
           */
          subtitle={paymentStep.subtitle}
          showAlert={false}
          showSuccess={true}
        />
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
            @media (max-width: 768px) {
              flex-direction: column;
              width: 100%;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              @media (max-width: 768px) {
                width: 100%;
                align-items: center;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                padding: 16px;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
                gap: 2px;
                align-self: stretch;
                border-radius: 8px;
                border: 1px solid ${colors.NEUTRAL_700};
                margin-bottom: 16px;
              `}
            >
              {isPlayerRegistration ? (
                <>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin-bottom: 8px;
                    `}
                  >
                    <Image
                      src={
                        getImageThumbnailId(session?.player) ||
                        '/images/generic_placeholders/placeholder_player.png'
                      }
                      // eslint-disable-next-line react-native/no-inline-styles
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        padding: 2,
                        backgroundColor: 'white',
                      }}
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src =
                          '/images/generic_placeholders/placeholder_competition.png'
                      }}
                      preview={false}
                    />
                    <p
                      css={css`
                        ${paragraphMediumStyle}
                        ${weightSemiBold}
                        overflow: hidden;
                        color: ${colors.WHITE};
                        text-overflow: ellipsis;
                      `}
                    >
                      {playerDisplayName}
                    </p>
                  </div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                    `}
                  >
                    <Image
                      preview={false}
                      src={
                        selectedTeam?.is_bucket
                          ? BUCKET_IMAGE_SRC
                          : getImageThumbnailId(selectedTeam) ||
                            '/images/generic_placeholders/placeholder_team.png'
                      }
                      // eslint-disable-next-line react-native/no-inline-styles
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: selectedTeam?.is_bucket ? 8 : 4,
                        padding: selectedTeam?.is_bucket ? 0 : 2,
                        backgroundColor: 'white',
                      }}
                      onError={(e) => {
                        e.target.onerror = null
                        e.target.src =
                          '/images/generic_placeholders/placeholder_competition.png'
                      }}
                    />
                    <p
                      css={css`
                        ${paragraphMediumStyle}
                        overflow: hidden;
                        color: ${colors.WHITE};
                        text-overflow: ellipsis;
                      `}
                    >
                      {team_name}
                    </p>
                  </div>
                </>
              ) : (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <Image
                    src={
                      getImageThumbnailId(selectedTeam) ||
                      '/images/generic_placeholders/placeholder_team.png'
                    }
                    // eslint-disable-next-line react-native/no-inline-styles
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 4,
                      padding: 2,
                      backgroundColor: 'white',
                    }}
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src =
                        '/images/generic_placeholders/placeholder_competition.png'
                    }}
                  />
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      align-items: flex-start;
                    `}
                  >
                    <p
                      css={css`
                        ${paragraphMediumStyle}
                        ${weightSemiBold}
                      overflow: hidden;
                        color: ${colors.WHITE};
                        text-overflow: ellipsis;
                      `}
                    >
                      {team_name}
                    </p>
                    <span
                      css={css`
                        ${paragraphSmallStyle}
                        ${weightSemiBold}
                        overflow: hidden;
                        color: ${colors.NEUTRAL_50};
                        text-overflow: ellipsis;
                      `}
                    >
                      {division_name}
                    </span>
                  </div>
                </div>
              )}
              <br />
              {!division?.single_payment && depositValue ? (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphSmallBoldStyle}
                      overflow: hidden;
                      color: ${colors.WHITE};
                      text-overflow: ellipsis;
                    `}
                  >
                    Deposit:{' '}
                    <span
                      css={css`
                        ${paragraphSmallStyle}
                        overflow: hidden;
                        color: ${colors.WHITE};
                        text-overflow: ellipsis;
                        ${hasCouponAppliedToDeposit &&
                        showsCoupons &&
                        'text-decoration: line-through'}
                      `}
                    >
                      {formatCurrency(depositValue, currencyCode)}
                    </span>
                    {hasCouponAppliedToDeposit && showsCoupons ? (
                      <span
                        css={css`
                          ${paragraphSmallStyle}
                          overflow: hidden;
                          color: ${colors.WHITE};
                          text-overflow: ellipsis;
                          margin-left: 8px;
                        `}
                      >
                        {formatCurrency(
                          depositValue -
                            (couponInfo?.deposit_discount_amount || 0),
                          currencyCode
                        )}
                      </span>
                    ) : null}
                  </p>
                </div>
              ) : null}
              {showsCoupons &&
              couponInfo &&
              couponInfo?.is_deposit &&
              couponInfo.deposit_discount_amount > 0 ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: row;
                    gap: 4px;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphSmallBoldStyle}
                      overflow: hidden;
                      color: ${colors.SUCCESS_100};
                      text-overflow: ellipsis;
                    `}
                  >
                    {couponInfo?.type === 'percentage'
                      ? `Coupon Code: ${couponInfo?.code}`
                      : `Coupon Code: ${couponInfo?.code}`}
                  </p>
                  {couponInfo?.deposit_discount_amount &&
                  couponInfo?.deposit_discount_amount > 0 ? (
                    <Text
                      variant='paragraphMedium'
                      color={colors.SUCCESS_100}
                      weight='semiBold'
                      style={`
                    align-self: flex-end;
                  `}
                    >
                      -{''}
                      {formatCurrency(
                        couponInfo?.deposit_discount_amount,
                        currencyCode
                      )}
                    </Text>
                  ) : null}
                </div>
              ) : null}
              <br />
              {!isZero ? (
                <p
                  css={css`
                    ${paragraphSmallBoldStyle}
                    overflow: hidden;
                    color: ${colors.WHITE};
                    text-overflow: ellipsis;
                  `}
                >
                  Cost:{' '}
                  <span
                    css={css`
                      ${paragraphSmallStyle}
                      overflow: hidden;
                      color: ${colors.WHITE};
                      text-overflow: ellipsis;
                      ${hasCouponAppliedToCost &&
                      showsCoupons &&
                      'text-decoration: line-through'}
                    `}
                  >
                    {isPlayerRegistration
                      ? formatCurrency(division?.player_cost, currencyCode)
                      : formatCurrency(division?.team_cost, currencyCode)}
                  </span>
                  {hasCouponAppliedToCost && showsCoupons ? (
                    <span
                      css={css`
                        ${paragraphSmallStyle}
                        overflow: hidden;
                        color: ${colors.WHITE};
                        text-overflow: ellipsis;
                        margin-left: 8px;
                      `}
                    >
                      {teamCost}
                    </span>
                  ) : null}
                </p>
              ) : null}
              {showsCoupons &&
              couponInfo &&
              couponInfo?.discount > 0 &&
              couponInfo?.is_installment ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: row;
                    gap: 4px;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphSmallBoldStyle}
                      overflow: hidden;
                      color: ${colors.SUCCESS_100};
                      text-overflow: ellipsis;
                    `}
                  >
                    {couponInfo?.type === 'percentage'
                      ? `Coupon Code: ${couponInfo?.code}`
                      : `Coupon Code: ${couponInfo?.code}`}
                  </p>
                  {couponInfo?.discount_amount &&
                  couponInfo?.discount_amount > 0 ? (
                    <Text
                      variant='paragraphMedium'
                      color={colors.SUCCESS_100}
                      weight='semiBold'
                      style={`
                    align-self: flex-end;
                  `}
                    >
                      -{''}
                      {formatCurrency(
                        couponInfo?.discount_amount,
                        currencyCode
                      )}
                    </Text>
                  ) : null}
                </div>
              ) : null}
              <br />
              <div>
                <p
                  css={css`
                    ${paragraphSmallBoldStyle}
                    overflow: hidden;
                    color: ${colors.WHITE};
                    text-overflow: ellipsis;
                    margin-bottom: 16px;
                  `}
                >
                  Registration Status:{' '}
                </p>
                <div
                  css={css`
                    .ant-steps {
                      padding: 0 0 0 0;
                    }
                    .ant-steps-item-title {
                      color: ${colors.WHITE} !important;
                    }
                    .ant-steps-item-description {
                      color: ${colors.STORM_GRAY} !important;
                    }
                    .ant-steps-item-wait .ant-steps-item-icon {
                      background-color: ${colors.NEUTRAL_600} !important;
                      border-color: ${colors.NEUTRAL_600} !important;
                    }
                    .ant-steps-item-wait
                      .ant-steps-item-icon
                      > .ant-steps-icon {
                      color: ${colors.STORM_GRAY} !important;
                    }
                  `}
                >
                  <Steps
                    direction='vertical'
                    size='small'
                    items={[
                      {
                        title: paymentStep.isLoading ? (
                          <RepPaymentStatusSkeleton line='title' />
                        ) : (
                          <Text
                            variant='paragraphSmall'
                            color={colors.WHITE}
                            weight='regular'
                          >
                            {paymentStep.title}
                          </Text>
                        ),
                        status: paymentStep.status,
                        icon:
                          paymentStep.icon === 'check' ? (
                            <CheckCircleOutlined
                              style={{ color: colors.SUCCESS_300 }}
                            />
                          ) : (
                            <ClockCircleOutlined
                              style={{ color: colors.WARNING_300 }}
                            />
                          ),
                        description: paymentStep.isLoading ? (
                          <RepPaymentStatusSkeleton line='description' />
                        ) : paymentStep.action ? (
                          <div css={repPaymentActionStyle}>
                            {paymentStep.description}
                            <Button
                              text={
                                paymentStep.action === 'pay'
                                  ? TEAM_BUDGET_PAYMENT_COPY.payNow
                                  : TEAM_BUDGET_PAYMENT_COPY.continuePayment
                              }
                              onClick={openRepPayment}
                              variant='primary'
                              style='flex: none;'
                            />
                          </div>
                        ) : (
                          paymentStep.description
                        ),
                      },
                      {
                        title: isApproved ? (
                          <Text
                            variant='paragraphSmall'
                            color={colors.WHITE}
                            weight='regular'
                          >
                            Registration Approved
                          </Text>
                        ) : (
                          <Text
                            variant='paragraphSmall'
                            color={colors.WHITE}
                            weight='regular'
                          >
                            Pending League Approval
                          </Text>
                        ),
                        status: isApproved
                          ? 'finish'
                          : paid
                          ? 'process'
                          : 'wait',
                        icon: isApproved ? (
                          <CheckCircleOutlined
                            style={{ color: colors.SUCCESS_300 }}
                          />
                        ) : paid ? (
                          <ClockCircleOutlined
                            style={{
                              color: paid
                                ? colors.WARNING_300
                                : colors.NEUTRAL_100,
                            }}
                          />
                        ) : undefined,
                        description: isApproved
                          ? ''
                          : paid
                          ? "You'll Receive A Confirmation Once It's Reviewed."
                          : '',
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
            {billing_schedule &&
            billing_schedule.length > 0 &&
            !division?.single_payment ? (
              <div
                css={css`
                  display: flex;
                  padding: 8px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                  border-radius: 8px;
                  background: ${colors.NEUTRAL_700};
                  margin-bottom: 16px;
                  /* on mobile the gap should be 8 */
                  @media (max-width: 768px) {
                    gap: 8px;
                  }
                `}
              >
                {/* Title */}
                <div
                  onClick={() =>
                    setInstallmentScheduleOpen(!installmentScheduleOpen)
                  }
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                  `}
                >
                  {installmentScheduleOpen ? (
                    <DownOutlined
                      size={24}
                      css={css`
                        color: ${colors.WHITE};
                      `}
                    />
                  ) : (
                    <RightOutlined
                      size={24}
                      css={css`
                        color: ${colors.WHITE};
                      `}
                    />
                  )}
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      color: ${colors.WHITE}
                    `}
                  >
                    Payment Schedule
                  </p>
                </div>
                {installmentScheduleOpen ? (
                  <div
                    css={css`
                      width: 100%;
                      height: 1px;
                      background: ${colors.CHARADE};
                    `}
                  />
                ) : null}
                {installmentScheduleOpen &&
                  billing_schedule?.map((b, index) => (
                    <div
                      key={index}
                      css={css`
                        display: flex;
                        padding: 0px 8px;
                        flex-direction: column;
                        justify-content: center;
                        align-items: flex-start;
                        gap: 8px;
                        align-self: stretch;
                      `}
                    >
                      <p
                        css={css`
                          ${paragraphSmallStyle}
                          color: ${colors.WHITE}
                        `}
                      >
                        <span
                          css={css`
                            ${paragraphSmallBoldStyle}
                          `}
                        >
                          {b?.payment_type === 'deposit'
                            ? 'Deposit'
                            : 'Payment'}{' '}
                          {b?.payment_type !== 'deposit' ? index + 1 : ''}:{' '}
                        </span>
                        {formatCurrency(b?.subtotal, currencyCode)} due{' '}
                        {formatDate(b?.due_date)}
                      </p>
                    </div>
                  ))}
                {installmentScheduleOpen ? (
                  <>
                    <div
                      css={css`
                        width: 100%;
                        height: 1px;
                        background: ${colors.CHARADE};
                      `}
                    />
                    <p
                      css={css`
                        ${paragraphSmallStyle}
                        color: ${colors.STORM_GRAY}
                      `}
                    >
                      All monthly payments will now be paid online.
                    </p>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          <div
            css={css`
              background: ${colors.NEUTRAL_700};
              min-height: 300px;
              width: 1px;
              /* change the orientation no mobile */
              @media (max-width: 768px) {
                width: 100%;
                height: 1px;
                min-height: 0;
              }
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 16px;
              flex: 1 0 0;
              /*
               * SN-4709 (Inon, Sep 2) — WITHOUT THIS THE SIDEBAR COLLAPSES.
               *
               * Both columns are flex: 1 0 0, so neither shrinks and each grows
               * to half the row. But a flex item also carries min-width: auto,
               * which floors it at its MIN-CONTENT width — and min-content is
               * inherited from the widest thing inside that cannot wrap.
               *
               * Every row here used to hold wrappable prose, so that floor was
               * tiny. The Invite Players row broke that: its share link is
               * white-space: nowrap (deliberately — a wrapped URL is what the
               * field replaced), so expanding it made this column demand ~830px
               * of a 1010px row. The registration sidebar has no such floor, so
               * it absorbed the whole overflow and crushed to 145px, wrapping
               * "Payment Approved" onto two clipped lines.
               *
               * min-width: 0 opts this column out of that floor, so the two
               * columns stay 50/50 and the link ellipsises inside its own field
               * instead — which is what ShareLinkField's head/tail split is for.
               * It must be repeated at every level down to that field
               * (nextRowStyle, the content wrapper, the share-link div): one
               * missing link restores the floor for the whole chain.
               */
              min-width: 0;
              @media (max-width: 768px) {
                width: 100%;
              }
            `}
          >
            <p
              css={css`
                ${headingStyle}
                color: ${colors.WHITE};
                text-align: center;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {"What's Next?"}
            </p>
            <NextContent
              title='Download the Mobile App'
              subtitle='Get notifications and updates about this registration.'
              content={
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    align-items: flex-start;
                    @media (max-width: 768px) {
                      width: 100%;
                    }
                  `}
                >
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      color: ${colors.WHITE};
                    `}
                  >
                    Download the mobile app to receive notifications and updates
                    on your registration.
                  </p>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      gap: 8px;
                      width: 100%;
                      align-items: center;
                      justify-content: center;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                      `}
                    >
                      <QRCode
                        value={androidAppLink}
                        bgColor={colors.HEADER}
                        bordered={true}
                        css={css`
                          margin-bottom: 4px;
                        `}
                      />
                      <p
                        css={css`
                          ${paragraphSmallStyle}
                          color: ${colors.WHITE};
                        `}
                      >
                        Android
                      </p>
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                      `}
                    >
                      <QRCode
                        bgColor={colors.HEADER}
                        bordered={true}
                        value={iosAppLink}
                        css={css`
                          margin-bottom: 4px;
                        `}
                      />
                      <p
                        css={css`
                          ${paragraphSmallStyle}
                          color: ${colors.WHITE};
                        `}
                      >
                        iOS
                      </p>
                    </div>
                  </div>
                </div>
              }
            />
            {!isPlayerRegistration ? (
              <NextContent
                title='Update Team Logo'
                subtitle='Add a crest so your team is recognisable on schedules and standings.'
                content={
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 8px;
                      align-items: flex-start;
                      width: 100%;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: flex-end;
                        gap: 8px;
                        flex: 1 0 0;
                        align-self: stretch;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          align-items: flex-start;
                          gap: 4px;
                          flex: 1 0 0;
                        `}
                      >
                        <Input
                          name='teamLogo'
                          placeholder=''
                          autoComplete='off'
                          autoCorrect='off'
                          autoCapitalize='none'
                          type='file'
                          id='team-logo'
                          accept={accept}
                          onChange={(e) => setNewTeamImage(e.target.files[0])}
                          css={css`
                            cursor: pointer;
                          `}
                          disabled={newTeamLoading || newTeamSuccess}
                        />
                      </div>
                      {newTeamImage ? (
                        <div
                          css={css`
                            margin-bottom: 2px;
                            border: 1px solid ${colors.ATTENDANCE_GRAY};
                            border-radius: 4px;
                            width: 40px;
                            height: 40px;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            padding: 4px;
                          `}
                        >
                          <Image
                            src={URL.createObjectURL(newTeamImage)}
                            preview={true}
                          />
                        </div>
                      ) : null}
                    </div>
                    {newTeamImage && !newTeamSuccess ? (
                      <button
                        css={css`
                          ${buttonStyle}
                          ${headingSixStyle}
                          width: 100%;
                          cursor: pointer;
                          align-self: stretch;
                        `}
                        onClick={async () => {
                          setNewTeamLoading(true)
                          setNewTeamError(null)
                          setNewTeamSuccess(null)
                          try {
                            await updateTeamLogo(team_id, newTeamImage)
                            setNewTeamSuccess('Team logo updated successfully')
                            // Refresh session data
                            const { data } = await req(
                              `/registration/session/${session?.id}`
                            )
                            setSession(data)
                          } catch (e) {
                            setNewTeamError(
                              e?.message ||
                                'An error occurred while updating the team logo'
                            )
                          } finally {
                            setNewTeamLoading(false)
                          }
                        }}
                        disabled={newTeamLoading}
                      >
                        Upload logo
                      </button>
                    ) : null}
                    {newTeamSuccess ? (
                      <Alert
                        message={'Logo Uploaded Successfully'}
                        type='success'
                        showIcon
                        css={css`
                          width: 100%;
                        `}
                      />
                    ) : null}
                    {newTeamError ? (
                      <Alert
                        message={newTeamError || 'An error occurred'}
                        type='error'
                        showIcon
                        css={css`
                          width: 100%;
                        `}
                      />
                    ) : null}
                  </div>
                }
              />
            ) : null}
            {/*
              The PLAYER counterpart of the team row below, and the two are
              mutually exclusive by construction: `isTeamRegistration` is
              `!isPlayerRegistration`, so exactly one of them can ever render.

              A player shares a `?team_id=` link that brings someone else onto
              the roster they just joined — there is no TeamRegistration of
              their own here, which is why `resolveShareLink` deliberately
              returns null for this role and the link is built from the shared
              URL builder instead.
            */}
            {canShareRegistration ? (
              <NextContent
                title='Share This Registration Link'
                subtitle='Invite someone else to register for this team.'
                onClick={() => setIsShareOpen(true)}
              />
            ) : null}
            {/*
              SN-4709 Team Privacy (Jeff, Sep 1) — the FIRST of the three places the share
              link surfaces. Team registrations only: a player has no team of their own to
              share, and showing them an empty card would be worse than showing nothing.

              Jeff's own framing for the private case: "note that this is a private team,
              so teams aren't going to show up on the list; if you want to change it you go
              here." Both halves of that are UI now rather than prose (Inon, Sep 2): the
              state is a badge beside the title, and "you go here" is a link to the Team
              Budget tab. What is left in words is the one thing neither can say — that
              this link is the only way onto the roster.
            */}
            {isTeamRegistration && shareLink ? (
              <NextContent
                title='Invite Players'
                titleAccessory={
                  confirmedTeam?.is_private ? <PrivateTeamBadge /> : null
                }
                subtitle={
                  confirmedTeam?.is_private
                    ? 'Only people with this link can join.'
                    : 'Send your players straight to your team.'
                }
                // The only row carrying something the rep has to ACT on. A link
                // nobody expands is a link nobody sends.
                defaultOpen
                content={
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 8px;
                      align-items: flex-start;
                      align-self: stretch;
                      /* The chain again — see the What's Next column. */
                      min-width: 0;
                    `}
                  >
                    {/*
                      One sentence, at paragraphSmall — NOT paragraphMedium. At
                      16px in pure WHITE this paragraph was the loudest thing on
                      a card whose own title is 14px, and it opened by restating
                      the subtitle directly above it. The privacy state is the
                      badge's job now; this says only what the badge cannot.
                    */}
                    <p
                      css={css`
                        ${paragraphSmallStyle}
                        color: ${colors.NEUTRAL_30};
                      `}
                    >
                      {confirmedTeam?.is_private
                        ? 'This link is the only way onto the roster — send it to the players you want.'
                        : 'Share this link with your players so they can register straight onto your team.'}
                    </p>
                    <div
                      data-testid='review-team-share-link'
                      css={css`
                        align-self: stretch;
                        min-width: 0;
                      `}
                    >
                      <ShareLinkField link={shareLink} label='Team Link' />
                    </div>
                    {confirmedTeam?.is_private ? (
                      /*
                        Jeff's "if you want to change it you go here" — a ROUTE,
                        so it is a link rather than a sentence about one. New tab
                        for the same reason every other row on this terminal
                        screen opens one: navigating away is blocked here.
                      */
                      <p
                        css={css`
                          ${paragraphXSmallStyle}
                          color: ${colors.NEUTRAL_70};
                        `}
                      >
                        Want it listed publicly?{' '}
                        <a
                          href={`${ROUTES.TEAM_ROOT}/${team_id}/registration`}
                          target='_blank'
                          rel='noreferrer'
                          css={inlineLinkStyle}
                        >
                          Make this team public
                        </a>{' '}
                        from the Team Budget tab.
                      </p>
                    ) : null}
                  </div>
                }
              />
            ) : null}
            {selectedTeam?.is_bucket ? null : (
              <NextContent
                route={resolveMyTeamRoute({
                  isTeamRegistration,
                  costDistributionEnabled: division?.cost_distribution_enabled,
                  teamId: team_id,
                  rootScheduleId: root_schedule_id,
                })}
                title='Go To My Team'
                subtitle='Manage your roster, players and team details.'
              />
            )}
            <NextContent
              route={`${ROUTES.SCHEDULE_ROOT}/${root_schedule_id}/standings`}
              title='Go To My Competition'
              subtitle='See the schedule, standings and other teams.'
            />
          </div>
        </div>
      </div>
      <WarningModal
        isOpen={isLeaveWarningOpen}
        onClose={() => setIsLeaveWarningOpen(false)}
        onConfirm={startNewRegistration}
        title={capitalizeWords('Registration Already Submitted')}
        description='Would you like to start a new registration instead?'
        cancelLabel='STAY HERE'
        confirmLabel='START OVER'
      />
      {canShareRegistration ? (
        <ShareModal
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          entityType='registration'
          entityName={shareTeamName}
          shareUrl={shareUrl}
          title={capitalizeWords('Share Registration Link')}
          // Sentence copy, not a label — Title Case is for titles and short
          // labels only, so the team's own casing carries through.
          subtitle={`Share this link with your friends to register for ${shareTeamName}`}
        />
      ) : null}
    </>
  )
}

export default RegisterReview
